import React, { FC } from 'react'
import cx from 'classnames'
import { useTranslation } from 'react-i18next'
import { Icon } from 'components/Icon'
import { Disclosure } from 'components/Disclosure'
import { Stack } from 'components/Stack'
import { LayerGroup as LayerGroupModel, LayerGroupTypes, Layer as LayerModel } from 'models/layer'
import { Layer } from '../Layer'
import { Flex } from 'components/Flex'
import { useUserContext } from 'context/user-context'
import { useProjectContext } from 'context/project-context'
import styles from './LayerGroup.module.scss'
import { UserRoles } from 'models/user'

type LayerGroupProps = {
  group: LayerGroupModel
  editing: LayerModel | null
  selectPredictiveModel: (predictiveModelId: string) => void
  selectPredictiveModelHistory: (predictiveModelId: string) => void
}

export const LayerGroup: FC<LayerGroupProps> = ({ group, editing, selectPredictiveModel, selectPredictiveModelHistory }) => {
  const { t } = useTranslation()
  const [{ activeLayers }] = useProjectContext()
  const [currentUser] = useUserContext()

  const classes = cx({
    [styles.layerGroup]: true,
    [styles.predictiveModel]: group.type === LayerGroupTypes.PREDICTIVE_MODEL,
    [styles.premiumLayer]: group.type === LayerGroupTypes.PREMIUM,
  })

  const title = (
    <Flex align="center" gap="sm" className={styles.title}>
      <Icon name={group.icon} />
      <span style={{ textAlign: "left" }}>{group.name}</span>
    </Flex>
  )

  const hasActiveLayers = group.layers.some((layer) => activeLayers.includes(layer.id))
  const isPredictiveModel = group.type === LayerGroupTypes.PREDICTIVE_MODEL
  const userHasRights = !currentUser!.isRole(UserRoles.STAFF)

  return (
    <Disclosure title={title} className={classes} defaultOpen={hasActiveLayers}>
      <Stack space="small">
        {group.layers.map((layer) => (
          <Layer
            key={layer.name}
            layer={layer}
            hasSelection={!!editing?.id}
            isSelected={layer.id === editing?.id}
          />
        ))}
        {isPredictiveModel && userHasRights && (
          <>
            <Flex justify="start" align="center" className={styles.predictiveReportIcon}>
              <Icon name="file-chart-line" />
              <button
                className={styles.predictiveReportButton}
                onClick={() => {
                  selectPredictiveModel(group.predictiveModelId!)
                }}
              >
                {t('panel.layers.generate_predictive_report')}
              </button>
            </Flex>
            <Flex justify="start" align="center" className={styles.predictiveReportIcon}>
              <Icon name="time-line" />
              <button
                className={styles.predictiveReportButton}
                onClick={() => {
                  selectPredictiveModelHistory(group.predictiveModelId!)
                }}
              >
                {t('panel.layers.previous_predictive_reports')}
              </button>
            </Flex>
          </>
        )}
      </Stack>
    </Disclosure>
  )
}

LayerGroup.displayName = 'LayerGroup'
